// Helpers for the per-customer recurring schedule editor.
// All times are Europe/Rome wall-clock; the day-offset is integer days
// relative to the delivery day (-7..0).

export const WEEKDAYS = [
  { value: 0, label: 'Domenica',  short: 'Dom' },
  { value: 1, label: 'Lunedì',    short: 'Lun' },
  { value: 2, label: 'Martedì',   short: 'Mar' },
  { value: 3, label: 'Mercoledì', short: 'Mer' },
  { value: 4, label: 'Giovedì',   short: 'Gio' },
  { value: 5, label: 'Venerdì',   short: 'Ven' },
  { value: 6, label: 'Sabato',    short: 'Sab' },
]

export const DAY_OFFSETS = [
  { value: 0,  label: 'Stesso giorno' },
  { value: -1, label: 'Il giorno prima' },
  { value: -2, label: 'Due giorni prima' },
  { value: -3, label: 'Tre giorni prima' },
  { value: -4, label: 'Quattro giorni prima' },
  { value: -5, label: 'Cinque giorni prima' },
  { value: -6, label: 'Sei giorni prima' },
  { value: -7, label: 'Una settimana prima' },
]

export const DEFAULT_SLOT = {
  name: '',
  menu: '',
  menuName: '',
  weekday: 1,        // Lunedì
  deliverAt: '12:00',
  openOffset: -1,
  openAt: '19:00',
  closeOffset: 0,
  closeAt: '09:30',
}

const HHMM = /^([0-1]\d|2[0-3]):[0-5]\d$/

export function isNewShape(slot) {
  return typeof slot.weekday === 'number' && !!slot.deliverAt
}

// Convert a legacy slot (deliveryD/H/M + openingHoursBefore/closingHoursBefore)
// into the new shape. The result includes a `_legacyImported` flag so the UI
// can show a "verifica gli orari" banner.
export function legacyToNew(slot) {
  if (isNewShape(slot)) return slot
  const dh = slot.deliveryH ?? 12
  const dm = slot.deliveryM ?? 0
  const dMin = dh * 60 + dm
  const oTotal = dMin - (slot.openingHoursBefore || 0) * 60
  const cTotal = dMin - (slot.closingHoursBefore || 0) * 60
  const split = totalMin => {
    const off = Math.floor(totalMin / 1440)
    const inDay = ((totalMin % 1440) + 1440) % 1440
    const h = Math.floor(inDay / 60)
    const m = inDay % 60
    return {
      offset: off,
      time: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`,
    }
  }
  const o = split(oTotal)
  const c = split(cTotal)
  return {
    ...slot,
    weekday: slot.deliveryD ?? 1,
    deliverAt: `${String(dh).padStart(2, '0')}:${String(dm).padStart(2, '0')}`,
    openOffset: o.offset,
    openAt: o.time,
    closeOffset: c.offset,
    closeAt: c.time,
    _legacyImported: true,
  }
}

// Strip transient UI flags before sending to backend.
export function stripUiFlags(slot) {
  const { _legacyImported, ...clean } = slot
  return clean
}

// Returns array of human-readable error strings (Italian). Empty if valid.
export function validateSlot(slot) {
  const errs = []
  if (!isNewShape(slot)) return errs // legacy: trust the server
  if (!Number.isInteger(slot.weekday) || slot.weekday < 0 || slot.weekday > 6)
    errs.push('Giorno consegna non valido.')
  if (typeof slot.deliverAt !== 'string' || !HHMM.test(slot.deliverAt))
    errs.push('Ora consegna non valida.')
  if (typeof slot.openAt !== 'string' || !HHMM.test(slot.openAt))
    errs.push('Ora apertura non valida.')
  if (typeof slot.closeAt !== 'string' || !HHMM.test(slot.closeAt))
    errs.push('Ora chiusura non valida.')
  if (
    !Number.isInteger(slot.openOffset) ||
    slot.openOffset < -7 ||
    slot.openOffset > 0
  )
    errs.push('Offset apertura non valido.')
  if (
    !Number.isInteger(slot.closeOffset) ||
    slot.closeOffset < -7 ||
    slot.closeOffset > 0
  )
    errs.push('Offset chiusura non valido.')
  if (errs.length) return errs
  const min = (off, hhmm) => {
    const [h, m] = hhmm.split(':').map(Number)
    return (off || 0) * 1440 + h * 60 + m
  }
  const dM = min(0, slot.deliverAt)
  const oM = min(slot.openOffset, slot.openAt)
  const cM = min(slot.closeOffset, slot.closeAt)
  if (!(oM < cM)) errs.push('L\'apertura deve precedere la chiusura.')
  if (!(cM <= dM)) errs.push('La chiusura non può essere dopo la consegna.')
  return errs
}

function weekdayLabel(weekday, offset) {
  const idx = ((weekday + offset) % 7 + 7) % 7
  return WEEKDAYS[idx].label
}

// "Domenica 19:00 → Lunedì 09:30 → Lunedì 12:00"
export function formatPreview(slot) {
  if (!isNewShape(slot)) return ''
  return [
    `${weekdayLabel(slot.weekday, slot.openOffset)} ${slot.openAt}`,
    `${weekdayLabel(slot.weekday, slot.closeOffset)} ${slot.closeAt}`,
    `${weekdayLabel(slot.weekday, 0)} ${slot.deliverAt}`,
  ].join(' → ')
}
